import { inject, Injectable } from '@angular/core';
import { ApiService, IGeoCell, IRecommendedTest } from '@libs/apis';
import { GeoTestsService } from '@portal/app/geo-designer/services/geo-tests.service';
import { BrandStoreService } from '@portal/app/dashboard/geo-doe-config/data-stores/brand-store.service';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { distinctUntilChanged, switchMap, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { groupBy } from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class GeoRecommendationsService {
  private readonly geoTestsService = inject(GeoTestsService);
  private readonly apiService = inject(ApiService);
  private readonly brandStoreService = inject(BrandStoreService);

  static getInitialConversionType(
    recommendedTests: IRecommendedTest[]
  ): number {
    const testableRecommendations = recommendedTests.filter(
      (recommendedTest) => recommendedTest.metadata?.isTestable
    );
    const testGroupedByConversionId = groupBy(
      testableRecommendations,
      'conversionTypeId'
    );
    const maxGroup = Object.entries(testGroupedByConversionId).reduce(
      (max, [key, groupedTests]) => {
        return groupedTests.filter((rec) => rec.isTestable).length > max.size
          ? { id: Number(key), size: groupedTests.length }
          : max;
      },
      { id: 0, size: 0 }
    );
    return maxGroup.id;
  }

  getRecommendedTests(
    conversionTypeId?: number
  ): Observable<IRecommendedTest[]> {
    return this.geoTestsService
      .getAllRecommendedTests()
      .pipe(
        map((recommendedTests) =>
          conversionTypeId
            ? this.filterTestsByConversionType(
                recommendedTests,
                conversionTypeId
              )
            : recommendedTests
        )
      );
  }

  private filterTestsByConversionType(
    recommendedTests: IRecommendedTest[],
    conversionTypeId: number
  ): IRecommendedTest[] {
    return recommendedTests
      .filter((test) => test.conversionTypeId === conversionTypeId)
      .map((test) => ({
        ...test,
        isTestable: test.metadata.isTestable
      }));
  }

  getReferenceData() {
    return this.geoTestsService.selectedConversionTypeId.pipe(
      distinctUntilChanged(),
      switchMap((conversionTypeId) => {
        if (!conversionTypeId) return of();
        return this.geoTestsService.getDSInfoForConversionType(
          conversionTypeId
        );
      })
    );
  }

  getGeoAllDsMarkets(conversionTypeId?: number): Observable<IGeoCell[]> {
    const { clientId, brandId } = this.brandStoreService.brand;
    if (conversionTypeId) {
      return fromPromise(
        this.apiService.geoDoeApisOperations.getGeoDsMarkets(
          clientId,
          brandId,
          conversionTypeId
        )
      );
    }

    return this.geoTestsService.selectedConversionTypeId.pipe(
      distinctUntilChanged(),
      switchMap((selectedConversionTypeId) => {
        if (!selectedConversionTypeId) return of();
        return fromPromise(
          this.apiService.geoDoeApisOperations.getGeoDsMarkets(
            clientId,
            brandId,
            selectedConversionTypeId
          )
        );
      })
    );
  }
}
